/**
 * Flow asset kind provider.
 *
 * Exported from @skaile/workspaces/factory-assets/connectors/flow/engine for explicit registration
 * by the CLI at bootstrap. Discovery does NOT auto-include this provider
 * (discovery must stay a leaf — no flow-engine imports).
 *
 * Implements IAssetKindProvider with flow-specific:
 * - Discovery patterns: *.flow.yaml, *.flow.json
 * - Manifest validation via FlowManifestSchema
 * - Requires extraction: nodes[].run.assets plus legacy skill/sub-flow references
 * - File filter: just the manifest file (flows are single-file)
 */

import * as path from "node:path";
import type {
  AssetKindFileFilter,
  AssetKindRequiresEdge,
  IAssetKindProvider,
} from "@skaile/workspaces/plugins";
import { validateFlow } from "./flow-manifest.js";

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const flowKindProvider: IAssetKindProvider = {
  kind: "flow",
  displayName: "Flow",
  providerVersion: "1.1.0",
  discoveryPatterns: ["**/*.flow.yaml", "**/*.flow.json"],

  matchesPath(relativePath: string): boolean {
    return relativePath.endsWith(".flow.yaml") || relativePath.endsWith(".flow.json");
  },

  validateManifest(data: unknown) {
    return validateFlow(data);
  },

  defaultFileFilter(manifestPath: string): AssetKindFileFilter {
    return {
      assetRoot: path.dirname(manifestPath),
      files: [path.basename(manifestPath)],
    };
  },

  extractRequires(assetRef: string, manifest: Record<string, unknown>): AssetKindRequiresEdge[] {
    const edges: AssetKindRequiresEdge[] = [];
    const seen = new Set<string>();
    const add = (to: string, field: string): void => {
      if (seen.has(to)) return;
      seen.add(to);
      edges.push({ from: assetRef, to, field });
    };

    const nodes = manifest.nodes;
    if (Array.isArray(nodes)) {
      for (const node of nodes) {
        if (typeof node !== "object" || node === null) continue;
        const n = node as Record<string, unknown>;
        const data = (
          typeof n.data === "object" && n.data !== null ? (n.data as Record<string, unknown>) : {}
        ) as Record<string, unknown>;
        const run =
          typeof n.run === "object" && n.run !== null ? (n.run as Record<string, unknown>) : {};

        if (Array.isArray(run.assets)) {
          for (const asset of run.assets) {
            if (typeof asset === "string") add(asset, "nodes[].run.assets");
          }
        }

        if (run.kind === "sub-flow" && typeof run.flow === "string") {
          const flowRef = run.flow.startsWith("flow:") ? run.flow : `flow:${run.flow}`;
          add(flowRef, "nodes[].run.flow");
        }

        const skill = typeof data.skill === "string" ? data.skill : n.skill;
        if (typeof skill === "string") {
          add(skill, "nodes[].data.skill");
        }

        const subFlow = typeof data.flow === "string" ? data.flow : n.flow;
        if (typeof subFlow === "string") {
          add(subFlow.startsWith("flow:") ? subFlow : `flow:${subFlow}`, "nodes[].data.flow");
        }
      }
    }

    return edges;
  },
};
